import React, { forwardRef, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { offset, UseFloatingOptions } from "@floating-ui/react";
import type { ReactDatePickerProps } from "react-datepicker";
import ReactDatePicker from "react-datepicker";
import MaskedInput from "react-text-mask";
import { AppConfigContext } from "../../core/AppConfigContext";
import { RefType } from "../../types";
import { genericMemo } from "../../utils";
import { floatingUIPortalId } from "../FloatingUIPortal";
import { InputContainer, InputContainerProps } from "../inputs";
import { CustomContainer } from "./CustomContainer";
import { Day } from "./CustomDay";
import { CustomHeader } from "./CustomHeader";
import DateFieldMask from "./DateFieldMask";
import { DatePickerContextProvider } from "./DatePickerContext";
import { PredefinedRangeType } from "./PredefinedDates";
import { useConvertFromServerFunction, useConvertToServerFunction } from "./utils";

import { ReactComponent as CalendarIcon } from "./icons/calendar-icon.svg";

import styles from "./styles.module.scss";

export type DatePickerValue = string | number;
export type DatePickerDateValue = Date | DatePickerValue;

export const defaultTimeValueStartOfDay = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};

export const defaultTimeValueEndOfDay = {
  hour: 23,
  minute: 59,
  second: 59,
  millisecond: 999,
};

export type DatePickerProps<
  WithRange extends boolean | undefined = undefined,
  WithMultiple extends boolean | undefined = undefined,
> =
  Omit<InputContainerProps<ReactDatePickerProps<WithRange, WithMultiple>, ReactDatePicker<WithRange, WithMultiple>>, "inputComponent" | "onChange" | "value" | "endDate" | "startDate" | "selectedDates" | "selected" | "highlightDates" | "holidays" | "excludeDates" | "minDate" | "maxDate">
  &
  {
    onChange?: (
      date: WithRange extends false | undefined
        ? (WithMultiple extends false | undefined ? DatePickerValue : DatePickerValue[] | null)
        : [ DatePickerValue, DatePickerValue ],
      event: React.SyntheticEvent<any> | undefined,
    ) => void;
    value?: DatePickerValue | null,
    endDate?: DatePickerDateValue | null,
    minDate?: DatePickerDateValue | null,
    maxDate?: DatePickerDateValue | null,
    startDate?: DatePickerDateValue | null,
    selectedDates?: DatePickerDateValue[] | null;
    highlightDates?: DatePickerDateValue[] | null;
    holidays?: { date: DatePickerDateValue | null, holidayName: string }[];
    excludeDates?: { date: DatePickerDateValue | null, message: string }[];
    headerActions?: ReactNode | ReactNode[]
    timeZone?: string
    monthsShown?: number
    defaultTimeValue?: {
      hour: number,
      minute: number,
      second: number,
      millisecond: number,
    }
    keepTimeValue?: boolean
    withConfirmation?: boolean
    predefinedRanges?: PredefinedRangeType[]
  };

const DatePickerComponent = <
  WithRange extends boolean | undefined = undefined,
  WithMultiple extends boolean | undefined = undefined,
>(props: DatePickerProps<WithRange, WithMultiple>) => <></>;

export const DatePicker = genericMemo(forwardRef(<
  WithRange extends boolean | undefined = undefined,
  WithMultiple extends boolean | undefined = undefined,
>(
  {
    headerActions,
    onChange,
    value,
    defaultTimeValue = defaultTimeValueEndOfDay,
    keepTimeValue,
    endDate,
    startDate,
    selectsRange,
    selectedDates,
    timeZone,
    highlightDates,
    holidays,
    excludeDates,
    monthsShown,
    predefinedRanges,
    withConfirmation,
    minDate,
    maxDate,
    ...props
  }: DatePickerProps<WithRange, WithMultiple>,
  ref: RefType<ReactDatePicker<WithRange, WithMultiple>>,
) => {
  const inputRef = useRef<MaskedInput | undefined>();
  const [ rootRef, setRootRef ] = useState<HTMLDivElement | null>();
  const [ internalValue, setInternalValue ] = useState<DatePickerValue | null | undefined>(value);
  const appConfig = useContext(AppConfigContext);
  const convertFromServer = useConvertFromServerFunction();
  const convertFunction = useConvertToServerFunction({
    timeZone,
    keepTimeValue,
    defaultTimeValue,
  });
  const wrappedHighlightDates = useMemo(() => highlightDates?.map(item => convertFromServer(item)), [ convertFromServer, highlightDates ]);
  const wrappedExcludedDays = useMemo(() => excludeDates?.map(day => {
    return {
      ...day,
      date: convertFromServer(day.date),
    };
  }), [ convertFromServer, excludeDates ]);

  const wrappedOnChange = useCallback((
    date: WithRange extends false | undefined
      ? (WithMultiple extends false | undefined ? Date | null : Date[] | null)
      : [ Date | null, Date | null ],
    event: React.SyntheticEvent<any> | undefined,
  ) => {
    if (withConfirmation) {
      if (Array.isArray(date)) {
        setInternalValue(date.map(convertFunction) as any);
      } else {
        setInternalValue(convertFunction(date, internalValue) as any);
      }
    } else {
      if (Array.isArray(date)) {
        onChange?.(date.map(convertFunction) as any, event);
        setInternalValue?.(date.map(convertFunction) as any);
      } else {
        onChange?.(convertFunction(date, internalValue) as any, event);
        setInternalValue?.(convertFunction(date, internalValue) as any);
      }
    }
  }, [ withConfirmation, convertFunction, internalValue, onChange ]);

  useEffect(() => {
    setInternalValue(value);
  }, [ value ]);

  return (
    <DatePickerContextProvider value={{
      value: convertFromServer(internalValue)!,
      onChange: wrappedOnChange as any,
      monthsShown,
      selectsRange,
      predefinedRanges,
    }}>
      <InputContainer<ReactDatePickerProps<WithRange, WithMultiple>, ReactDatePicker<WithRange, WithMultiple>>
        mapPropsToInput={({ allProps, inputProps: { ...inputProps } }) => ({
          ...inputProps,
        }) as ReactDatePickerProps<WithRange, WithMultiple>}
        inputComponent={ReactDatePicker}
        customInput={<DateFieldMask additionalRef={inputRef} />}
        inputContainerProps={{
          className: styles.inputContainer,
        }}
        renderDayContents={(day, date) => {
          return (
            <Day>
              {day}
            </Day>
          );
        }}
        containerProps={{ ref: setRootRef }}
        popperProps={{
          elements: {
            reference: rootRef,
          },
        } as UseFloatingOptions}
        showPopperArrow={false}
        popperModifiers={[
          offset(0),
        ]}
        startAdornment={<CalendarIcon />}
        calendarContainer={CustomContainer}
        renderCustomHeader={(
          {
            monthDate,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => {
          return (
            <CustomHeader
              actions={headerActions}
              monthDate={monthDate}
              decreaseMonth={decreaseMonth}
              increaseMonth={increaseMonth}
              prevMonthButtonDisabled={prevMonthButtonDisabled}
              nextMonthButtonDisabled={nextMonthButtonDisabled}
            />
          );
        }
        }
        portalId={floatingUIPortalId}
        popperClassName={styles.root}
        popperPlacement="bottom-start"
        ref={ref}
        dateFormat={props.dateFormat || appConfig.dateFormat}

        {...props}
        selected={convertFromServer(internalValue)}
        onChange={wrappedOnChange}
        endDate={convertFromServer(endDate)}
        startDate={convertFromServer(startDate)}
        minDate={convertFromServer(minDate)}
        maxDate={convertFromServer(maxDate)}
        selectsRange={selectsRange}
        monthsShown={monthsShown}
        highlightDates={wrappedHighlightDates as any}
        holidays={holidays as any}
        excludeDates={wrappedExcludedDays as any}
      />
    </DatePickerContextProvider>
  );
}) as typeof DatePickerComponent);
